import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Radom, { DepositBeneficiaryDetails, Deposit as IDeposit, ManagedBalanceLedger } from '../../state/Radom'
import TimeText from '../../components/TimeText'
import { Container } from '../../components/Animations'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import { getMethod, getMethodLogoByTicker, ManagedPaymentMethod } from '../../util/Managed'
import BreadCrumbs from '../../components/Breadcrumbs'
import { useParams, useSearchParams } from 'react-router-dom'
import { RADOM_COLORS } from '../../util/Constants'
import Spinner from '../../components/Spinner'
import { Chevron } from '../../icons/Chevron'
import { IconButton } from '../../components/Button'
import Copy from '../../icons/Copy'
import { errorToast, shouldRound, successToast, trimZeros } from '../../util/Util'
import { SectionContent, SectionItem, SubTitle, Section, SectionBreaker } from '../../components/Shared'
import _ from 'lodash'

const Deposit = (): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger>()
  const [isLoading, setIsLoading] = useState(false)
  const [deposit, setDeposit] = useState<IDeposit>()
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  const [beneficiaryDetails, setBeneFiciaryDetails] = useState<DepositBeneficiaryDetails>()
  const isFiatDeposit = useMemo(() => !!deposit?.depositMethod.Fiat, [deposit])

  const fetchDepositData = async (id: string): Promise<void> => {
    setIsLoading(true)
    let deposit: IDeposit
    let isFiat: boolean
    try {
      deposit = await Radom.getDeposit(id)
      isFiat = !!deposit?.depositMethod.Fiat
      setDeposit(deposit)
      setPaymentMethod(getMethod(
        deposit.depositMethod.Crypto?.network || deposit.depositMethod.Fiat?.onrampNetwork || '',
        deposit.depositMethod.Crypto?.token || deposit.depositMethod.Fiat?.onrampToken
      ))
    } catch {
      setIsLoading(false)
      errorToast('Failed to load deposit')
      return
    }

    try {
      const ledger = await Radom.listManagedBalanceLedger([deposit.id])
      if (ledger.length > 0) { setManagedBalanceLedger(ledger[0]) }
    } catch {
      errorToast('Failed to load ledger')
    }

    if (isFiat) {
      try {
        const event = await Radom.getDepositEvent(deposit.id)
        const data = event.eventData.deposit.beneficiaryDetails
        setBeneFiciaryDetails(data)
      } catch {
        errorToast('Failed to load deposit event for fiat deposit')
      }
    }
    setIsLoading(false)
  }

  const debouncedFetchDepositData = useMemo(() => _.debounce(fetchDepositData, 300), [])

  useEffect(() => {
    if (!params.id) return
    debouncedFetchDepositData(params.id)
    return () => debouncedFetchDepositData.cancel()
  }, [params.id])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (!deposit || !paymentMethod) {
    return <></>
  }

  return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <BreadCrumbs items={
        source === 'transactions'
          ? [
            { item: 'Transactions', to: '/transactions' },
            { item: deposit.id, to: `/deposit/${deposit.id}?source=transactions` }
          ]
          : source === 'virtual_accounts'
            ? [
              { item: 'Virtual Accounts', to: '/virtual_accounts' },
              { item: deposit.id, to: `/deposit/${deposit.id}?source=virtual_accounts` }
            ]
            : [
              { item: 'Deposits', to: '/deposits' },
              { item: deposit.id, to: '.' }
            ]} />
    </div>
    <Section>
      <div style={{
        fontSize: '18px',
        paddingBottom: 5,
        display: 'flex',
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: '32px'
        }}>
          {isFiatDeposit && <>
            <img src={getMethod('Fiat', deposit.depositMethod.Fiat?.fiatCurrency).logo as string} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
            <span>{deposit.fiatGrossAmount}</span>
            <span style={{
              background: RADOM_COLORS.GRAY_MED,
              color: RADOM_COLORS.BLACK,
              borderRadius: 10,
              fontSize: 14,
              padding: '5px 10px',
              fontWeight: 400

            }}>{getMethod('Fiat', deposit.depositMethod.Fiat?.fiatCurrency).ticker}</span>
            <Chevron fill={RADOM_COLORS.GRAY_DARKER} style={{ width: 16, height: 16, transform: 'rotate(-90deg)' }} />
          </>}
          <img src={paymentMethod.logo} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
          <span>{deposit.grossAmount}</span>
          <span style={{
            background: RADOM_COLORS.GRAY_MED,
            color: RADOM_COLORS.BLACK,
            borderRadius: 10,
            fontSize: 14,
            padding: '4px 10px',
            fontWeight: 400

          }}>{paymentMethod.ticker}</span>
        </div>
      </div>

      <SectionContent>
        <SectionItem>
          <SubTitle>ID</SubTitle>
          <div style={{
            display: 'flex',
            gap: 4
          }}>
            <span>{deposit.id}</span>
            <IconButton style={{ padding: 0 }}>
              <Copy style={{ width: 17, stroke: RADOM_COLORS.GRAY_DARKER }} onClick={(e) => {
                e.stopPropagation()
                navigator.clipboard
                  .writeText(deposit.id)
                  .then(() => {
                    successToast(
                      'Copied deposit event id'
                    )
                  })
                  .catch((err) => {
                    console.error('copy failed', err)
                    errorToast(
                      'Failed to copy deposit event id'
                    )
                  })
              }} />
            </IconButton>
          </div>
        </SectionItem>
        <SectionItem>
          <SubTitle>Type</SubTitle>
          <span>{deposit.depositMethod.Crypto ? 'Cypto deposit' : 'Fiat deposit'}</span>
        </SectionItem>
        <SectionItem>
          <SubTitle>Deposit date</SubTitle>
          <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(deposit.depositedAt)} />
        </SectionItem>
      </SectionContent>
    </Section>

    {
      managedBalanceLedger &&
        <Section>
          <SubTitle style={{ fontSize: 18 }}>
                Ledger
          </SubTitle>
          <SectionContent>
            <SectionItem>
              <SubTitle>Balance Before</SubTitle>
              <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                <span>
                  {
                    trimZeros(shouldRound(paymentMethod.ticker)
                      ? Number(managedBalanceLedger.balanceBefore).toFixed(2)
                      : managedBalanceLedger.balanceBefore)
                  }
                </span>
                <span style={{ color: RADOM_COLORS.GRAY4 }}>
                  {paymentMethod.ticker ?? paymentMethod.logo} on {managedBalanceLedger.network}
                </span>
              </div>
            </SectionItem>
            <SectionItem>
              <SubTitle>Balance After</SubTitle>
              <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                <span>
                  {
                    trimZeros(shouldRound(paymentMethod.ticker)
                      ? Number(managedBalanceLedger.balanceAfter).toFixed(2)
                      : managedBalanceLedger.balanceAfter)
                  }
                </span>
                <span style={{ color: RADOM_COLORS.GRAY4 }}>
                  {paymentMethod.ticker ?? paymentMethod.logo} on {managedBalanceLedger.network}
                </span>
              </div>
            </SectionItem>
          </SectionContent>
        </Section>
    }

    {isFiatDeposit && beneficiaryDetails && (
      <Section>
        <SubTitle style={{ fontSize: 18 }}>Sender details</SubTitle>
        <SectionContent>
          {Object.entries(beneficiaryDetails)
            .filter(([key, val]) => val != null && val !== '' && key !== 'recipient_name') // dont show recipient name because thats often bridge sp zoo
            .map(([key, val], idx) => {
              const label = key
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')

              return (
                <SectionItem key={idx}>
                  <SubTitle>{label}</SubTitle>
                  <span>{val}</span>
                </SectionItem>
              )
            })}
        </SectionContent>
      </Section>
    )}

    <Section>
      <SubTitle style={{ fontSize: 18 }}>
          Deposit details
      </SubTitle>

      <SectionContent>
        <SectionItem>
          <SubTitle>Asset</SubTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <PaymentMethodDisplay
              style={{ marginRight: 4 }}
              paymentMethod={paymentMethod}
              method={{ network: deposit.depositMethod.Crypto?.network || 'Fiat' }}
              includeNetworkName
              disableTooltip={false} />
          </div>
        </SectionItem>
        {
          deposit.depositMethod.Fiat &&
            <SectionItem>
              <SubTitle>Fiat gross deposit</SubTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>

                <PaymentMethodDisplay
                  method={{ network: 'Fiat', token: deposit.depositMethod.Fiat.fiatCurrency }}
                  excludeName />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                  <span>{deposit.fiatGrossAmount}</span>
                  <span>{getMethod('Fiat', deposit.depositMethod.Fiat.fiatCurrency).ticker}</span>
                </div>
              </div>
            </SectionItem>
        }

        <SectionItem>
          <SubTitle>Gross deposit</SubTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
            {
              deposit.depositMethod.Crypto && <>
                <PaymentMethodDisplay
                  style={{ marginRight: 4 }}
                  method={{
                    network: deposit.depositMethod.Crypto.network,
                    token: deposit.depositMethod.Crypto.token
                  }}
                  excludeName
                  disableTooltip={false} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                  <span>{deposit.grossAmount}</span>
                  <span>{getMethod(
                    deposit.depositMethod.Crypto.network,
                    deposit.depositMethod.Crypto.token).ticker}</span>
                </div>
              </>
            }

            {
              deposit.depositMethod.Fiat && <>
                <PaymentMethodDisplay
                  style={{ marginRight: 4 }}
                  method={{
                    network:
                      deposit.depositMethod.Fiat.onrampNetwork,
                    token: deposit.depositMethod.Fiat.onrampToken
                  }}
                  excludeName />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                  <span>{deposit.grossAmount}</span>
                  <span>{getMethod(
                    deposit.depositMethod.Fiat.onrampNetwork,
                    deposit.depositMethod.Fiat.onrampToken).ticker}</span>
                </div>
              </>
            }
          </div>
        </SectionItem>

        <SectionItem>
          <SubTitle>Net deposit</SubTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
            {
              deposit.depositMethod.Crypto && <>
                <PaymentMethodDisplay
                  style={{ marginRight: 4 }}
                  method={{
                    network: deposit.depositMethod.Crypto.network,
                    token: deposit.depositMethod.Crypto.token
                  }}
                  excludeName
                  disableTooltip={false} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                  <span>{deposit.netAmount}</span>
                  <span>{getMethod(
                    deposit.depositMethod.Crypto.network,
                    deposit.depositMethod.Crypto.token).ticker}</span>
                </div>
              </>
            }

            {
              deposit.depositMethod.Fiat && <>
                <PaymentMethodDisplay
                  style={{ marginRight: 4 }}
                  method={{
                    network:
                      deposit.depositMethod.Fiat.onrampNetwork,
                    token: deposit.depositMethod.Fiat.onrampToken
                  }}
                  excludeName />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                  <span>{deposit.netAmount}</span>
                  <span>{getMethod(
                    deposit.depositMethod.Fiat.onrampNetwork,
                    deposit.depositMethod.Fiat.onrampToken).ticker}</span>
                </div>
              </>
            }
          </div>
        </SectionItem>

        <SectionItem>
          <SubTitle>Fee</SubTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
            {
              deposit.depositMethod.Crypto && <>
                <PaymentMethodDisplay
                  style={{ marginRight: 4 }}
                  method={{
                    network: deposit.depositMethod.Crypto.network,
                    token: deposit.depositMethod.Crypto.token
                  }}
                  excludeName
                  disableTooltip={false} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                  <span>{deposit.fee}</span>
                  <span>{getMethod(
                    deposit.depositMethod.Crypto.network,
                    deposit.depositMethod.Crypto.token).ticker}</span>
                </div>
              </>
            }

            {
              deposit.depositMethod.Fiat && <>
                <PaymentMethodDisplay
                  style={{ marginRight: 4 }}
                  method={{
                    network:
                      deposit.depositMethod.Fiat.onrampNetwork,
                    token: deposit.depositMethod.Fiat.onrampToken
                  }}
                  excludeName />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                  <span>{deposit.fee}</span>
                  <span>{getMethod(
                    deposit.depositMethod.Fiat.onrampNetwork,
                    deposit.depositMethod.Fiat.onrampToken).ticker}</span>
                </div>
              </>
            }

          </div>
        </SectionItem>

        <SectionItem>
          <SubTitle>Deposited at</SubTitle>
          <div>
            <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(deposit.depositedAt)} />
          </div>
        </SectionItem>

      </SectionContent>
      <SectionBreaker />

      {
        deposit.conversionRates.length > 0 &&
                    <div>
                      <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Conversion rates</SubTitle>
                      {
                        deposit.conversionRates.map((d, i) =>
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <span>1</span>
                              {
                                getMethodLogoByTicker(d.from) &&
                                <img src={getMethodLogoByTicker(d.from)} style={{ width: 15, height: 'auto' }} />
                              }
                              {d.from}
                            </div>
                            <div>=</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <span>{Number(d.rate)}</span>
                              {
                                getMethodLogoByTicker(d.to) &&
                                <img src={getMethodLogoByTicker(d.to)} style={{ width: 15, height: 'auto' }} />
                              }
                              {d.to}
                            </div>
                          </div>
                        )}
                    </div>
      }
    </Section>

  </Container >
}

export default Deposit
